import React, { Fragment, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Channel } from "../../../../lib/api/channels";
import { SectionLoader } from "../../../../components/LogoLoader";
import { useBoardStore } from "../../../../stores/boardStore";
import { useAuthStore } from "../../../../stores/authStore";

/**
 * PseudoChannelPickerModal
 *
 * Flat picker for selecting which top-level entity (e.g. a Meta or TikTok ad
 * account) to attach to the board as a pseudo-channel. Opens when the user
 * picks a pseudo-eligible channel from Add Channel. Each picked entity creates
 * its own BoardChannel row on the backend, with all of its descendants
 * auto-selected.
 */

interface PseudoChannelPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    channel: Channel | null;
    /** Source entity IDs already attached to the board (shown disabled). */
    addedEntityIds?: string[];
    /** Called with the picked source entity ID. Parent attaches the pseudo-channel. */
    onPick: (sourceEntityId: string) => void;
}

export const PseudoChannelPickerModal: React.FC<PseudoChannelPickerModalProps> = ({
    isOpen,
    onClose,
    channel,
    addedEntityIds = [],
    onPick,
}) => {
    const { t, i18n } = useTranslation("common");
    const { finance, currencySettings } = useBoardStore();
    const { user } = useAuthStore();
    const [searchQuery, setSearchQuery] = useState("");

    React.useEffect(() => {
        if (!isOpen) setSearchQuery("");
    }, [isOpen]);

    const getEntitiesArray = (chan: Channel | null): unknown[] => {
        if (!chan) return [];
        const raw = (chan as unknown as Record<string, unknown>).entities;
        if (Array.isArray(raw)) return raw;
        if (raw && typeof raw === "object" && Array.isArray((raw as Record<string, unknown>).entities)) {
            return (raw as Record<string, unknown>).entities as unknown[];
        }
        return [];
    };

    const rootEntities = useMemo(() => {
        if (!channel) return [];
        return getEntitiesArray(channel).filter((e: unknown) => {
            const entity = e as Record<string, unknown>;
            return !entity.parentId;
        });
    }, [channel]);

    // Financial totals per entity (aggregated with children)
    const entityTotals = useMemo(() => {
        const totals: Record<string, number> = {};
        if (!finance) return totals;

        const source = finance.breakdown;
        if (source && Array.isArray(source)) {
            source.forEach((item: unknown) => {
                const rec = item as Record<string, unknown>;
                const entityId = String(rec.entityId || rec.entity_id || "");
                if (!entityId || entityId === "unallocated") return;
                const type = (String(rec.type || rec.transactionType || "")).toUpperCase();
                const value = parseFloat(String(rec.value || rec.amount || 0));
                if (totals[entityId] === undefined) totals[entityId] = 0;
                if (type === "EXPENSE") totals[entityId] -= Math.abs(value);
                else if (type === "REVENUE") totals[entityId] += value;
            });
        }
        return totals;
    }, [finance]);

    const getAggregatedValue = React.useCallback((entityId: string): number | null => {
        let total = entityTotals[entityId] || 0;
        let has = entityTotals[entityId] !== undefined;
        const entities = channel ? getEntitiesArray(channel) : [];
        entities
            .filter((e: unknown) => String((e as Record<string, unknown>).parentId) === String(entityId))
            .forEach((child: unknown) => {
                const childVal = getAggregatedValue(String((child as Record<string, unknown>).id));
                if (childVal !== null) { total += childVal; has = true; }
            });
        return has ? total : null;
    }, [entityTotals, channel]);

    const currencyCode = currencySettings?.default || user?.currency || "";
    const symbol = (() => {
        try {
            return (0).toLocaleString(i18n.language, { style: "currency", currency: currencyCode, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/[\d\s.,]/g, "");
        } catch { return "$"; }
    })();
    const formatValue = (val: number) =>
        Math.abs(val).toLocaleString(i18n.language, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

    const getChildrenInfo = (entityId: string): { count: number; label: string } => {
        const entities = channel ? getEntitiesArray(channel) : [];
        const children = entities.filter((e: unknown) => String((e as Record<string, unknown>).parentId) === String(entityId));
        if (children.length === 0) return { count: 0, label: "" };
        const typeCounts: Record<string, number> = {};
        children.forEach((child: unknown) => {
            const ct = String((child as Record<string, unknown>).entityType || "item");
            typeCounts[ct] = (typeCounts[ct] || 0) + 1;
        });
        const mostCommon = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "item";
        const normalizedKey = mostCommon.replace(/_/g, "");
        const label = t(`entityTypes.${normalizedKey}`, { count: children.length }) || mostCommon.replace(/_/g, " ");
        return { count: children.length, label };
    };

    const renderChannelIcon = () => {
        if (!channel) return null;
        if (channel.icon && (channel.icon.startsWith("http") || channel.icon.startsWith("/"))) {
            return <img src={channel.icon} alt={channel.name} className="w-6 h-6 object-contain" />;
        }
        if (channel.channelTypeConfig?.icon && (channel.channelTypeConfig.icon.startsWith("/") || channel.channelTypeConfig.icon.startsWith("http"))) {
            return <img src={channel.channelTypeConfig.icon} alt={channel.name} className="w-6 h-6 object-contain" />;
        }
        if (channel.icon) return <span className="text-xl leading-none">{channel.icon}</span>;
        return <span className="w-6 h-6 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4e2f2f0b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4e2f2f0b.svg)`, maskRepeat: "no-repeat", WebkitMaskRepeat: "no-repeat", maskPosition: "center", WebkitMaskPosition: "center", maskSize: "contain", WebkitMaskSize: "contain", backgroundColor: "currentColor", display: "inline-block" }} role="img" />;
    };

    const isLoading = !channel || (channel as unknown as Record<string, unknown>).isLoadingEntities === true;

    const filtered = useMemo(() => {
        return rootEntities
            .filter((e: unknown) => {
                const name = String((e as Record<string, unknown>).name || "");
                return name.toLowerCase().includes(searchQuery.toLowerCase());
            })
            .sort((a: unknown, b: unknown) => {
                const valA = Math.abs(getAggregatedValue(String((a as Record<string, unknown>).id)) || 0);
                const valB = Math.abs(getAggregatedValue(String((b as Record<string, unknown>).id)) || 0);
                return valB - valA;
            });
    }, [rootEntities, searchQuery, getAggregatedValue]);

    if (!channel) return null;

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 modal-backdrop" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="relative bg-white dark:bg-neutral-900 border border-transparent dark:border-neutral-800 rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden transform">
                                <div className="flex flex-col" style={{ height: "75vh" }}>
                                    {/* Header */}
                                    <div className="flex-shrink-0 px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 z-10">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white dark:bg-neutral-800 rounded-full border border-neutral-200 dark:border-neutral-700 text-xl overflow-hidden shadow-sm">
                                                    {renderChannelIcon()}
                                                </div>
                                                <div className="flex-1 min-w-0 flex flex-col justify-center">
                                                    <h2 className="text-base font-bold text-neutral-600 dark:text-neutral-100 leading-none mb-0.5 truncate">
                                                        {channel.name}
                                                    </h2>
                                                    <p className="text-xs text-neutral-400 dark:text-neutral-500 font-medium truncate">
                                                        {t("itemSelection.selectAccount")}
                                                    </p>
                                                </div>
                                            </div>
                                            <button onClick={onClose} className="flex-shrink-0 ml-3 p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors focus:outline-none">
                                                <span className="w-5 h-5 text-neutral-500 dark:text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`, maskRepeat: "no-repeat", WebkitMaskRepeat: "no-repeat", maskPosition: "center", WebkitMaskPosition: "center", maskSize: "contain", WebkitMaskSize: "contain", backgroundColor: "currentColor", display: "inline-block" }} role="img" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Search */}
                                    <div className="flex-shrink-0 px-6 pb-2 pt-2 bg-white dark:bg-neutral-950 border-b border-neutral-100 dark:border-neutral-800">
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span className="h-4 w-4 text-neutral-400 dark:text-neutral-500" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_9bf91cd6.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_9bf91cd6.svg)`, maskRepeat: "no-repeat", WebkitMaskRepeat: "no-repeat", maskPosition: "center", WebkitMaskPosition: "center", maskSize: "contain", WebkitMaskSize: "contain", backgroundColor: "currentColor", display: "inline-block" }} role="img" />
                                            </div>
                                            <input
                                                type="text"
                                                className="block w-full pl-9 pr-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm bg-neutral-50 dark:bg-neutral-900 placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:bg-white dark:focus:bg-neutral-800 focus:ring-1 focus:ring-accent-primary focus:border-accent-primary transition-colors dark:text-neutral-100"
                                                placeholder={t("common.search") || "Search..."}
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 overflow-y-auto min-h-0 p-6">
                                        {isLoading ? (
                                            <div className="flex flex-col items-center justify-center py-12 h-full">
                                                <SectionLoader size="sm" message={String(t("channels.loadingEntities"))} />
                                            </div>
                                        ) : filtered.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center py-12 text-center text-neutral-500 h-full">
                                                <p>{t("channels.noEntitiesFound")}</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                <AnimatePresence mode="popLayout">
                                                    {filtered.map((raw: unknown) => {
                                                        const entity = raw as Record<string, unknown>;
                                                        const entityId = String(entity.id);
                                                        const entityName = String(entity.name || "");
                                                        const entityType = String(entity.entityType || "");
                                                        const childrenInfo = getChildrenInfo(entityId);
                                                        const isAdded = addedEntityIds.includes(entityId);

                                                        return (
                                                            <motion.button
                                                                layout
                                                                initial={{ opacity: 0, y: 10 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.1 } }}
                                                                transition={{ duration: 0.2 }}
                                                                key={entityId}
                                                                onClick={() => { if (!isAdded) onPick(entityId); }}
                                                                disabled={isAdded}
                                                                className={`w-full group border-2 border-neutral-200 dark:border-neutral-800 rounded-xl p-4 transition-colors duration-200 text-left ${isAdded ? "bg-neutral-50 dark:bg-neutral-900/40 opacity-60 cursor-not-allowed" : "bg-white dark:bg-neutral-900 hover:border-accent-primary/50 hover:bg-accent-primary/5"}`}
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-accent-primary/10 transition-colors">
                                                                        {renderChannelIcon()}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                                                                            {entityName}
                                                                        </h3>
                                                                        <p className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
                                                                            <span className="truncate">
                                                                                {childrenInfo.count > 0
                                                                                    ? `${childrenInfo.count} ${childrenInfo.label}`
                                                                                    : t(`entityTypes.${entityType.replace(/_/g, "")}`) || entityType.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase()) || t("channels.noElements")
                                                                                }
                                                                            </span>
                                                                            {isAdded && (
                                                                                <span className="flex-shrink-0 bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-neutral-200 dark:border-neutral-700">
                                                                                    {t("common.alreadySelected")}
                                                                                </span>
                                                                            )}
                                                                        </p>
                                                                    </div>

                                                                    {(() => {
                                                                        const val = getAggregatedValue(entityId);
                                                                        if (val === null) return null;
                                                                        const isNeg = val < 0;
                                                                        return (
                                                                            <div className={`text-xs font-bold whitespace-nowrap flex-shrink-0 ${isNeg ? "text-rose-600" : "text-emerald-600"}`}>
                                                                                {symbol}{formatValue(val)}
                                                                            </div>
                                                                        );
                                                                    })()}

                                                                    {isAdded ? (
                                                                        <div className="flex-shrink-0 text-emerald-500">
                                                                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_02c569e3.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_02c569e3.svg)`, maskRepeat: "no-repeat", WebkitMaskRepeat: "no-repeat", maskPosition: "center", WebkitMaskPosition: "center", maskSize: "contain", WebkitMaskSize: "contain", backgroundColor: "currentColor", display: "inline-block" }} role="img" />
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-sm font-medium text-accent-primary">
                                                                            {t("boards.addToBoard")}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </motion.button>
                                                        );
                                                    })}
                                                </AnimatePresence>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
